import { HomepageProductCard } from "@modules/Homepage/HomepageProductCard";
import { SearchNotFoundRecommendation } from "@modules/Navbar/components/SearchNotFoundRecommendation";
import { useTranslation } from "react-i18next";
import type { Product } from "@shopify/hydrogen-react/storefront-api-types";
import type { SearchProductsQuery } from "@gql/types";

type Props = {
  searchQuery: string;
  loading: boolean;
  products: SearchProductsQuery["search"]["nodes"] | undefined;
  error: Error | null;
  hasNextPage: boolean;
};

export const SearchModalProductsGrid: React.FC<Props> = ({
  searchQuery,
  loading,
  products,
  error,
  hasNextPage,
}) => {
  const { t } = useTranslation("Navbar");
  const normalizedProducts = (products ?? []).map(
    ({ basePriceVariants, ...product }) => ({
      product: {
        ...product,
        images: { nodes: product.featuredImage ? [product.featuredImage] : [] },
      },
      basePriceVariants: basePriceVariants.nodes,
    }),
  );

  if (loading && !products?.length) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-40-dynamic pb-10 pt-6 max-laptop:grid-cols-40-fixed max-mobile:grid-cols-15 max-mobile:px-[calc(100%/15)] max-mobile:py-6 *:col-span-padded">
          <p
            className="col-span-padded mt-8 text-center text-small text-text-3 max-mobile:col-span-full"
            role="status"
            aria-live="polite"
          >
            {t("loading")}
          </p>
        </div>
      </div>
    );
  }

  if (error) return null;

  if (!loading && !normalizedProducts.length && searchQuery.length > 1) {
    return <SearchNotFoundRecommendation searchQuery={searchQuery} />;
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="grid grid-cols-40-dynamic pb-10 pt-6 max-laptop:grid-cols-40-fixed max-mobile:grid-cols-15 max-mobile:px-[calc(100%/15)] max-mobile:py-6 *:col-span-padded">
        <div className="col-span-padded w-full max-mobile:col-span-full">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {normalizedProducts.map(({ product, basePriceVariants }) => (
              <div
                key={product.id + product.title}
                data-search-product-handle={product.handle}
                className="h-full"
              >
                <HomepageProductCard
                  product={product as Product}
                  basePriceVariants={basePriceVariants}
                  productImageClassName="h-full w-full object-cover"
                  productItemClassName="h-full w-full min-w-0 max-w-none gap-4 rounded-[24px] border border-border-5 bg-background-7 p-3 transition-all hover:shadow-lg"
                  productItemTitleClassName="text-small leading-smallMedium"
                  productPriceClassName="text-small leading-smallMedium"
                  imageWidth={220}
                  imageHeight={320}
                  imageFetchPriority="eager"
                />
              </div>
            ))}
          </div>

          {loading && hasNextPage && (
            <p
              className="mt-8 text-center text-small text-text-3"
              role="status"
              aria-live="polite"
            >
              {t("loading")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
